<script setup lang="ts">
import { computed, ref } from "vue";
import { useApiClient } from "@/api/client";
import { useExplorerStore } from "@/modules/explorer/store";
import dayjs from "dayjs";
import DocumentDO = App.Data.Explorer.DocumentDO;
import AiAssistQuestionDO = App.Data.AiAssist.AiAssistQuestionDO;
import { v4 as uuidv4 } from "uuid";
import { toastApiResult } from "@/toaster";

const chatResponse = ref<chatBubble[]>([]);
const chatSessionUuid = ref<string>(uuidv4());

const isChatDialogVisible = defineModel<boolean>();

type chatBubble = AiAssistQuestionDO & {
	owner: "user" | "ai";
	is_loading?: boolean;
	loading_state?: string;
	is_error?: boolean;
};

const isFeedbackDialogVisible = ref<boolean>(false);
const activeChatBubble = ref<chatBubble>();

const createChatBubble = (
	owner: "user" | "ai",
	is_loading: boolean = false,
	aiAssistQuestion: AiAssistQuestionDO
): chatBubble => {
	return {
		...{ owner, is_loading, is_error: false },
		...aiAssistQuestion,
	};
};

const userQuestion = ref<string>("");
const chatAllowed = ref(true);
const submitQuestion = async () => {
	chatAllowed.value = false;

	const questionBubble = createChatBubble("user", false, {
		uuid: null,
		chat_session_uuid: chatSessionUuid.value,
		user_question: userQuestion.value,
		user: null,
		ai_answer: null,
		document_configuration: null,
		ai_answer_was_helpful: null,
		written_feedback: null,
		created_at: new Date().toISOString(),
	});

	const loadingBubble = createChatBubble("ai", true, {
		uuid: null,
		chat_session_uuid: chatSessionUuid.value,
		user_question: "",
		user: null,
		ai_answer: null,
		document_configuration: null,
		ai_answer_was_helpful: null,
		written_feedback: null,
		created_at: new Date().toISOString(),
	});

	chatResponse.value.push(questionBubble);
	chatResponse.value.push(loadingBubble);

	userQuestion.value = "";
	const lastBubble = chatResponse.value.at(-1);
	await useApiClient("aiAssist").processQuestion(
		questionBubble,
		(token) => {
			lastBubble.is_loading = false;
			lastBubble.ai_answer = (lastBubble.ai_answer || "") + token;
		},
		(uuid) => {
			lastBubble.uuid = uuid;
		},
		(loadingState) => {
			lastBubble.loading_state = loadingState;
		},
		(document) => {
			loadingBubble.document_configuration = document;
		},
		(fullAnswer) => {
			lastBubble.is_loading = false;
			lastBubble.ai_answer = fullAnswer;
		},
		(error) => {
			lastBubble.is_loading = false;
			lastBubble.ai_answer = error;
		}
	);

	chatAllowed.value = true;
};

const chatResponseCompute = computed(() => chatResponse.value.toReversed());

const loadDocumentInExplorer = (document: DocumentDO) => {
	useExplorerStore().document = document;
	isChatDialogVisible.value = false;
};

const markHelpfulStatus = async (chatBubble: chatBubble, helpful: boolean) => {
	const api = useApiClient("aiAssist");

	const response = helpful
		? await api.markResponseAsHelpful(chatBubble.uuid)
		: await api.markResponseAsNotHelpful(chatBubble.uuid);

	if (response.isOk()) {
		chatBubble.ai_answer_was_helpful = helpful;
		if (!helpful) {
			isFeedbackDialogVisible.value = true;
			activeChatBubble.value = chatBubble;
		}
	}
};

const storeWrittenFeedback = async (chatBubble: chatBubble) => {
	const response = await toastApiResult(
		useApiClient("aiAssist").storeWrittenFeedback(chatBubble)
	);
	if (response.isOk()) {
		isFeedbackDialogVisible.value = false;
	}
};

const newChatSession = () => {
	chatSessionUuid.value = uuidv4();
	chatResponse.value = [];
};
</script>

<template>
	<v-dialog
		v-model="isFeedbackDialogVisible"
		scrim="#000"
		width="500"
		height="500"
	>
		<v-card elevation="0" class="help">
			<v-toolbar style="border-radius: 0 !important" color="white" flat>
				<v-toolbar-title
					><i class="fa-light fa-list-check"></i>
					User Feedback
				</v-toolbar-title>
				<v-spacer></v-spacer>

				<v-btn icon @click="isFeedbackDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider />

			<div class="pa-3">
				<div class="text-caption">
					Please tell us what missed the mark so we can improve. What did you
					expect, and what was incorrect or missing? Your feedback helps us
					train the AI to interpret your questions more accurately.
				</div>
				<v-divider class="my-3"></v-divider>
				<h6 class="">
					<v-textarea
						autofocus
						hide-details
						clearable
						no-resize
						v-model="activeChatBubble.written_feedback"
						density="compact"
						variant="filled"
						rows="6"
						max-rows="6"
						placeholder="Your Feedback..."
					/>
				</h6>
			</div>
			<v-divider />
			<v-card-actions>
				<v-btn
					color="error"
					variant="text"
					@click="isFeedbackDialogVisible = false"
					>No Thanks</v-btn
				>
				<v-btn
					color="primary"
					variant="flat"
					@click="storeWrittenFeedback(activeChatBubble)"
					>Save Feedback</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>
	<v-dialog
		v-model="isChatDialogVisible"
		scrim="#FFFFFF"
		width="60vw"
		height="75vh"
		class="blur-overlay-dialog"
	>
		<v-card class="h-100" elevation="1">
			<v-toolbar color="#FFFFFF">
				<v-icon class="ml-5" size="30">fa: fa-light fa-microchip-ai</v-icon>
				<v-toolbar-title>AI Assist</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-btn icon @click="isChatDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider />
			<div class="d-flex flex-column chats">
				<div class="parent px-10" style="min-height: 200px">
					<div class="chat">
						<template
							v-for="(bubble, index) in chatResponseCompute"
							:key="index"
						>
							<div class="w-100 d-flex pb-5" v-if="bubble.owner === 'user'">
								<v-spacer />
								<v-card
									class="pa-3 text-caption user-border"
									style="max-width: 40vw"
									variant="tonal"
									color="teal"
									elevation="1"
								>
									<div>
										{{ bubble.user_question }}
									</div>
									<div class="d-flex mt-2">
										<v-spacer />
										<div>
											{{ dayjs(bubble.created_at).format("HH:mm") }}
										</div>
									</div>
								</v-card>
							</div>
							<div v-else>
								<v-hover v-slot="{ isHovering, props }">
									<div class="w-100 d-flex pb-5" v-bind="props">
										<v-card
											class="pa-3 text-caption"
											style="max-width: 40vw"
											variant="tonal"
											:color="bubble.is_error ? 'error' : 'primary'"
											border
											elevation="1"
										>
											<v-skeleton-loader
												width="30vw"
												:loading="bubble.is_loading"
												type="list-item-three-line"
											>
												<v-responsive>
													<div class="typing-text">
														{{ bubble.ai_answer }}
													</div>
												</v-responsive>
											</v-skeleton-loader>
											<div
												class="d-flex mt-2"
												style="height: 20px; overflow: hidden"
											>
												<div
													v-if="bubble.is_loading"
													class="loading-state shimmer ml-5"
												>
													{{ bubble.loading_state }}
												</div>
												<v-spacer />
												<div class="timestamp">
													{{ dayjs(bubble.created_at).format("HH:mm") }}
												</div>
											</div>
										</v-card>

										<v-fade-transition>
											<div
												v-if="isHovering && !bubble.is_loading && bubble.uuid"
												class="ml-2 d-flex align-center ga-2"
											>
												<div class="d-flex pl-3" style="height: 30px">
													<v-btn
														:disabled="!bubble.document_configuration"
														@click="
															loadDocumentInExplorer(
																bubble.document_configuration
															)
														"
														v-tooltip="`Visualize in explorer`"
														size="30"
														icon
														variant="tonal"
														color="teal"
													>
														<v-icon size="18"
															>fa: fa-solid fa-chart-simple</v-icon
														>
													</v-btn>
													<v-divider vertical class="mx-3" />
													<v-btn
														:variant="
															bubble.ai_answer_was_helpful === false
																? 'flat'
																: 'text'
														"
														v-tooltip="`Mark this response as 'NOT helpful'`"
														size="30"
														icon
														color="error"
														class="mr-3"
														@click="markHelpfulStatus(bubble, false)"
													>
														<v-icon size="18"
															>fa: fa-light fa-thumbs-down</v-icon
														>
													</v-btn>
													<v-btn
														:variant="
															bubble.ai_answer_was_helpful === true
																? 'flat'
																: 'text'
														"
														v-tooltip="`Mark this response as 'very helpful'`"
														size="30"
														icon
														color="success"
														@click="markHelpfulStatus(bubble, true)"
													>
														<v-icon size="18">fa: fa-light fa-thumbs-up</v-icon>
													</v-btn>
												</div>
											</div>
										</v-fade-transition>

										<v-spacer />
									</div>
								</v-hover>
							</div>
						</template>
						<v-fade-transition>
							<div
								class="w-100 d-flex pb-5"
								v-if="chatResponseCompute.length === 0"
							>
								<v-spacer />
								<v-card
									class="pa-3 text-caption"
									style="max-width: 30vw"
									variant="tonal"
									color="warning"
									elevation="1"
								>
									<div>
										Hi there! 👋 I'm here to help you explore your data. You can
										ask me things like: • “What was the total revenue last
										month?” • “What is the average maximum occupancy per parking
										lot this year?.” • “Show me the search conversion percentage
										per search environment this month so far!” Ask away!
									</div>
								</v-card>
								<v-spacer />
							</div>
						</v-fade-transition>
					</div>
				</div>
				<div class="flex-shrink-0" style="height: 70px">
					<v-text-field
						autofocus
						class="mx-5 mt-3 text-caption"
						rounded
						v-model="userQuestion"
						variant="outlined"
						color="teal"
						hide-details
						:placeholder="
							chatAllowed ? 'Enter your query here' : 'AI is thinking..'
						"
						:disabled="!chatAllowed"
						@keyup.enter="submitQuestion"
					>
						<template v-slot:prepend-inner>
							<v-icon class="mr-5">fa: fa-light fa-comments</v-icon>
						</template>
						<template v-slot:append-inner>
							<v-fade-transition>
								<template v-if="userQuestion.length > 0">
									<v-btn
										size="30"
										icon
										variant="flat"
										color="teal"
										@click="submitQuestion"
									>
										<v-icon size="17">fa: fa-solid fa-paper-plane-top</v-icon>
									</v-btn>
								</template>
								<template v-else>
									<v-btn
										v-tooltip="`New Chat Session`"
										size="30"
										icon
										variant="text"
										color="teal"
										@click="newChatSession"
									>
										<v-icon size="17">fa: fa-light fa-plus</v-icon>
									</v-btn>
								</template>
							</v-fade-transition>
						</template>
					</v-text-field>
				</div>
			</div>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss">
.chats {
	height: calc(100% - 70px);
}
.blur-overlay-dialog.v-overlay--active {
	height: 100%;
	background-color: rgba(255, 255, 255, 0.2) !important;
	animation: blurIn 250ms forwards ease-out;
}

:deep(.v-overlay__content) {
	height: 100%;
	width: 50rem;
}

.parent {
	display: flex;
	flex-direction: column;
	flex-grow: 1;
}

.chat {
	flex: 1;
	white-space: pre-wrap;
	list-style: none;
	display: flex;
	flex-flow: column-reverse;
	overflow-y: auto;
	scrollbar-width: none;
	overscroll-behavior: contain;

	& li#message {
		box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
	}
}

.toolbar {
	padding-left: 1.5rem;
	padding-bottom: 0.5rem;
	display: flex;
	margin-bottom: 2rem;
}

.user-border {
	border: thin solid rgba(teal, var(–v-border-opacity));
}

.typing-text {
	display: inline-block;
	opacity: 0;
	animation: fade-in 0.3s linear forwards;
}

.loading-state {
	font-size: 12px;
	justify-content: left;
	color: #888;
	font-style: italic;
	height: 20px;
	position: relative;
}

.shimmer::after {
	content: "";
	position: absolute;
	top: 0;
	left: -150%;
	height: 100%;
	width: 150%;
	background: linear-gradient(
		to right,
		transparent 0%,
		rgba(255, 255, 255, 0.7) 80%,
		transparent 100%
	);
	animation: shimmer 1.6s infinite;
}

@keyframes shimmer {
	0% {
		left: -150%;
	}
	100% {
		left: 100%;
	}
}

@keyframes fade-in {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

@keyframes blurIn {
	0% {
		backdrop-filter: blur(0px);
	}
	100% {
		backdrop-filter: blur(2px);
	}
}
</style>
